import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, IconButton, Paper, Typography, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

/**
 * Conversation panel with chat interface
 * 
 * Features:
 * - Message history display
 * - Auto-scroll to bottom
 * - User/assistant message bubbles
 * - Input field with send button
 * - Loading state
 */
const ConversationPanel = ({ messages, onSendMessage, isProcessing = false }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || isProcessing) return;
    onSendMessage(input.trim());
    setInput('');
  };

  // Enter sends, Shift+Enter makes a newline.
  //
  // onKeyDown, not onKeyPress: onKeyPress is deprecated in React and does not
  // fire dependably on the textarea MUI renders for a multiline TextField, so
  // Enter did nothing and the only way to send was the button. The guard on
  // isComposing keeps an IME candidate selection (Japanese, Chinese, Korean)
  // from being swallowed as a send — Enter there confirms the candidate.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #e5e7eb',
          bgcolor: 'white',
          flexShrink: 0
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 1 }}>
          💬 Conversation
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280' }}>
          Tell me about your experience
        </Typography>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start' }}>
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Thinking...
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 2.5,
          borderTop: '1px solid #e5e7eb',
          bgcolor: 'white',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-end',
            bgcolor: '#f9fafb',
            border: '1.5px solid #e5e7eb',
            borderRadius: 1.5,
            p: 0.5,
            pr: 0.5,
            transition: 'all 0.2s',
            '&:focus-within': {
              bgcolor: 'white',
              borderColor: 'primary.main',
              boxShadow: '0 0 0 3px rgba(107, 140, 94, 0.1)'
            }
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your response..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isProcessing}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                px: 2,
                py: 1,
                fontSize: '15px',
                color: '#111827',
                '& ::placeholder': {
                  color: '#9ca3af'
                }
              }
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim() || isProcessing}
            sx={{
              background: 'linear-gradient(135deg, #6b8c5e 0%, #435a37 100%)',
              color: 'white',
              borderRadius: 1,
              px: 2,
              py: 1,
              '&:hover': {
                background: 'linear-gradient(135deg, #567347 0%, #36472d 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(107, 140, 94, 0.3)'
              },
              '&:disabled': {
                opacity: 0.5,
                color: 'white',
                background: 'linear-gradient(135deg, #6b8c5e 0%, #435a37 100%)'
              },
              transition: 'all 0.2s'
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

// Message bubble component
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        maxWidth: '85%',
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        animation: 'fadeIn 0.3s ease',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          ...(isUser
            ? {
                background: 'linear-gradient(135deg, #6b8c5e 0%, #435a37 100%)',
                color: 'white',
                borderBottomRightRadius: 4
              }
            : {
                bgcolor: 'white',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
                borderBottomLeftRadius: 4,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
              })
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '15px',
            lineHeight: 1.5,
            whiteSpace: 'pre-line'
          }}
        >
          {message.content}
        </Typography>
      </Paper>
    </Box>
  );
};

export default ConversationPanel;
